import { useTranslation } from 'react-i18next';
import { useCheckResponse } from '@src/hooks';
import { Button, message } from 'antd';
import { useSetPasswordMutation } from '@src/services/profile/profileApiSlice';
import { PageWrapper } from '@src/components';
import { FormPassword } from '@src/components/form';
import { FieldValues, useForm } from 'react-hook-form';

interface SetPasswordProps {
  onClose: () => void;
}

export function SetPassword({ onClose }: Readonly<SetPasswordProps>) {
  const { t } = useTranslation();
  const { checkResponse } = useCheckResponse();
  const [setPassword, { isLoading }] = useSetPasswordMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useForm({ mode: 'onChange' });

  const onSubmit = async (formData: FieldValues) => {
    try {
      await setPassword({
        password: formData.newPassword,
      }).unwrap();

      message.success(t('notification.success.setPassword'));
      onClose?.();
    } catch (error: any) {
      checkResponse(error);
    }
  };

  const handleDiscard = () => {
    reset();
    onClose?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageWrapper rootClassName='bg-wl-background text-wl-on-background py-6 px-4'>
        <p className='text-M-16 mb-6'>{t('pages.setPassword.description')}</p>
        <div className='grid sm:grid-flow-row gap-6'>
          <FormPassword
            t={t}
            control={control}
            name='newPassword'
            placeholder={t('forms.password')}
            error={errors.newPassword}
            showRules
            className='border-none'
          />

          <FormPassword
            t={t}
            control={control}
            name='confirmNewPassword'
            placeholder={t('forms.confirmPassword')}
            error={errors.confirmNewPassword}
            watch={watch}
            confirm
            referenceField='newPassword'
            className='border-none'
          />
        </div>
      </PageWrapper>
      <div className='flex justify-between gap-4 mt-8'>
        <Button className='btn-secondary w-1/2' onClick={handleDiscard}>
          {t('buttons.discard')}
        </Button>
        <Button
          htmlType='submit'
          className='btn-primary w-1/2'
          loading={isLoading}
        >
          {t('buttons.save')}
        </Button>
      </div>
    </form>
  );
}
